import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  parseSiteAvailability,
  unavailableSiteAvailability,
  type SiteAvailability,
} from './siteAvailability';

const SiteAvailabilityContext = createContext<SiteAvailability>(unavailableSiteAvailability);

export function SiteAvailabilityProvider({ children }: { children: ReactNode }) {
  const [availability, setAvailability] = useState(unavailableSiteAvailability);

  useEffect(() => {
    const controller = new AbortController();

    void fetch('/api/site-availability', {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Availability request failed: ${response.status}`);
        return response.json() as Promise<unknown>;
      })
      .then((value) => setAvailability(parseSiteAvailability(value)))
      .catch(() => {
        // Fail closed. Abort errors and network failures both leave every
        // restricted product unavailable.
      });

    return () => controller.abort();
  }, []);

  return createElement(SiteAvailabilityContext.Provider, { value: availability }, children);
}

/**
 * Product availability deliberately starts unavailable. Server-rendered
 * content therefore contains no restricted products, and network or parsing
 * failures keep them hidden.
 */
export function useSiteAvailability(): SiteAvailability {
  return useContext(SiteAvailabilityContext);
}
